/**
 * What the signed-in person's role may do, as the web app understands it.
 *
 * The API answers `GET /v1/auth/me/permissions` with the CASL grants of the
 * principal in the current workspace (`action:Subject` strings, recomputed on
 * every request — a role edited a minute ago applies on the next call). This
 * file is the ONE place the web reads those strings, and it reads them by the
 * same rule the API enforces: `permissionCovers` in
 * `apps/api/src/auth/casl/permission-vocabulary.ts`.
 *
 *   - `manage` is the action wildcard: `manage:Recipient` covers
 *     `create:Recipient`, `read:Recipient`, …
 *   - `all` is the subject wildcard: `manage:all` covers everything.
 *   - `read:Recipient` covers neither `manage:Recipient` nor `read:Person`.
 *   - Anything that is not exactly `action:Subject` grants nothing — the API
 *     refuses `manage:all:x`, so the web must not read it as `manage:all`.
 *
 * Two properties keep this honest:
 *
 *   1. It is NOT the security boundary. The API refuses the call whatever this
 *      returns; a wrong answer here hides or disables a control that would
 *      have failed, or shows one the server will refuse with a 403.
 *   2. It is pure. Loading and error states live in `hooks/use-permissions.ts`
 *      (which fails OPEN: an unknown answer shows the screen as it always was)
 *      — never here, where `null` permissions simply mean "nothing granted".
 *
 * The subject is a plain string on purpose: the API's vocabulary grows (the
 * Lms* subjects and `Role` landed with custom roles) and the web must not need
 * a release to understand a new grant. The subjects the web ASKS about are
 * pinned against the API's vocabulary file by
 * `lib/auth/__tests__/nav-permissions.spec.ts`.
 */

export const PERMISSION_ACTIONS = ['manage', 'create', 'read', 'update', 'delete'] as const

export type PermissionAction = (typeof PERMISSION_ACTIONS)[number]

/** A subject from the API vocabulary (`ABILITY_SUBJECTS`), e.g. `'Recipient'`. */
export type PermissionSubject = string

/** One thing a screen needs: `{ action: 'read', subject: 'Recipient' }`. */
export interface PermissionRequirement {
  action: PermissionAction
  subject: PermissionSubject
}

const ACTION_SET: ReadonlySet<string> = new Set(PERMISSION_ACTIONS)

/** A subject is an identifier: letters, digits and `_` (`Phishing_SendingDomain`). */
const SUBJECT_SHAPE = /^[A-Za-z][A-Za-z0-9_]*$/

/**
 * Parse one held grant, or null when it is not exactly `action:Subject` with a
 * known action. Strict like the API's `parsePermission`: no trimming, no case
 * folding, no extra colons.
 */
export function parsePermission(raw: unknown): PermissionRequirement | null {
  if (typeof raw !== 'string') return null
  const parts = raw.split(':')
  if (parts.length !== 2) return null
  const [action, subject] = parts
  if (!ACTION_SET.has(action) || !SUBJECT_SHAPE.test(subject)) return null
  return { action: action as PermissionAction, subject }
}

/** `{ action: 'read', subject: 'Recipient' }` → `'read:Recipient'`. */
export function permissionString(requirement: PermissionRequirement): string {
  return `${requirement.action}:${requirement.subject}`
}

/**
 * Does `held` (one grant the principal carries) cover `wanted`? The API's
 * `permissionCovers`, spelled the same way.
 */
export function permissionCovers(held: unknown, wanted: PermissionRequirement): boolean {
  const parsed = parsePermission(held)
  if (!parsed) return false
  const actionOk = parsed.action === 'manage' || parsed.action === wanted.action
  const subjectOk = parsed.subject === 'all' || parsed.subject === wanted.subject
  return actionOk && subjectOk
}

/**
 * May a principal holding `permissions` do `action` on `subject`?
 *
 * `null`/`undefined` permissions grant nothing: this function answers about a
 * KNOWN list. Whether an unknown list should show a control is the caller's
 * decision (the hooks fail open) — not something to hide in here.
 */
export function can(
  permissions: readonly string[] | null | undefined,
  action: PermissionAction,
  subject: PermissionSubject,
): boolean {
  if (!permissions || permissions.length === 0) return false
  const wanted: PermissionRequirement = { action, subject }
  return permissions.some((held) => permissionCovers(held, wanted))
}

/** True when the principal holds at least one of `requirements` (an empty list asks for nothing). */
export function canAny(
  permissions: readonly string[] | null | undefined,
  requirements: readonly PermissionRequirement[],
): boolean {
  if (requirements.length === 0) return true
  return requirements.some((r) => can(permissions, r.action, r.subject))
}
